import { AgGridReact } from 'ag-grid-react'
import { useCallback, useEffect, useState } from 'react'
import { Col, Form, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import CreatableSelect from 'react-select/creatable'
import { getLocaleText } from '../../../actions/datagrid'
import { codeItemAdd, codeListGet, tagManagerPersist } from '../../../api'
import { AttachmentView } from '../../../common/attachments/attachmentview'
import { Button } from '../../../common/Button.tsx'
import { PersonSelect } from '../../../common/personselect'
import { creatableSelectCodeSettings, creatableSelectTagSettings } from '../../../common/reactselect'
import { dateFormatter, dateGetter, fetchTags, starFormatter } from '../../../common/utils'
import { actionTypeSelector, authSelector } from '../../../reducers'

// code lists
const skillCodes = 'competenceSkills'

export function AddSkill({ show, setShow, skillData, saveData }) {
	const { t } = useTranslation()
	const actionType = useSelector(actionTypeSelector)
	const auth = useSelector(authSelector)

	const path = window.location.pathname
	const isPersonal = path.startsWith('/personal')

	// react-select
	const handleCodeItem = (newValue, actionMeta) =>
		setData({ ...data, [actionMeta.name]: { codeItemId: newValue.value } })

	const [skillOptions, setSkillOptions] = useState([])

	const fetchSkills = useCallback(async () => {
		const res = await codeListGet(skillCodes)
		setSkillOptions(
			res.data.items
				? res.data.items
						.map((item) => ({
							value: item.codeItemId, // item.key
							label: item.codeValue, // item.description
						}))
						.sort((a, b) => a.label.localeCompare(b.label))
				: [],
		)
	}, [])

	const handleCreateSkill = async (value) => {
		const res = await codeItemAdd(skillCodes, { codeValue: value })
		const { codeItemId, codeValue } = res.data
		setSkillOptions([...skillOptions, { value: codeItemId, label: codeValue }])
		setData({
			...data,
			skillCodeItem: { codeItemId: codeItemId },
		})
	}

	const [tagOptions, setTagOptions] = useState([])

	const handleTags = (options) =>
		setData({
			...data,
			tags: options.map((option) => ({ codeItemId: option.value, codeKey: option.label })),
		})

	const handleCreateTag = async (value) => {
		const res = await tagManagerPersist([{ codeKey: value }])
		const { codeItemId, codeKey } = res.data[0]
		setTagOptions([...tagOptions, { value: codeItemId, label: codeKey }])
		const currentTags = data.tags ?? []
		setData({
			...data,
			tags: [...currentTags, { codeItemId: codeItemId, codeKey: codeKey }],
		})
	}

	// fetch code lists
	useEffect(() => {
		fetchSkills()
		fetchTags(setTagOptions)
	}, [fetchSkills])

	const [data, setData] = useState(null)

	useEffect(() => setData(skillData), [skillData])

	const dataChange = ({ target }) => {
		const value = target.type === 'checkbox' ? target.checked : target.value
		setData({ ...data, [target.id]: value })
	}

	// react-select
	const selectChange = (newValue, actionMeta) => setData({ ...data, [actionMeta.name]: newValue.value })

	const handleSave = () => saveData(data)
	const handleClose = () => setShow(false)

	const columnDefs = [
		{
			headerName: t('Date', 'Päiväys'),
			field: 'updateInfoJSON.modifiedDateTime',
			filter: 'agDateColumnFilter',
			valueGetter: dateGetter,
			valueFormatter: dateFormatter,
		},
		{
			headerName: t('SkillLevel', 'Osaamistaso'),
			field: 'skillLevel',
			valueFormatter: starFormatter,
		},
	]

	let title
	if (data?.skillId > 0) {
		title = data?.skillCodeItem?.codeValue ?? t('Skill', 'Taito')
	} else {
		title = t('AddSkill', 'Lisää taito')
	}

	return (
		show &&
		data && (
			<Modal show onHide={handleClose} backdrop="static" size="xl" fullscreen="lg-down" scrollable>
				<Modal.Header closeButton>
					<Modal.Title>{title}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<Row className="mb-1">
						<PersonSelect
							disabled={data?.skillId > 0 || isPersonal}
							selectedPerson={data}
							selectChange={selectChange}
							supervised={auth.role === 'SUPERVISOR'}
						/>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="skillCodeItem">
							<Form.Label>{t('Skill', 'Taito')}</Form.Label>
							<CreatableSelect
								name="skillCodeItem"
								inputId="skillCodeItem"
								value={skillOptions.find((option) => option.value === data?.skillCodeItem?.codeItemId)}
								onChange={handleCodeItem}
								onCreateOption={handleCreateSkill}
								isValidNewOption={(inputValue) => inputValue.length > 0 && auth.knowledgeAdmin}
								options={skillOptions}
								{...creatableSelectCodeSettings(t)}
							/>
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="skillLevel">
							<Form.Label>{t('SkillLevel', 'Taso')}</Form.Label>
							<Form.Control
								type="number"
								required
								min="1"
								max="5"
								placeholder={t('SkillLevel', 'Taso')}
								onChange={dataChange}
								value={data?.skillLevel ?? ''}
							/>
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="skillDescription">
							<Form.Label>{t('Description', 'Kuvaus')}</Form.Label>
							<Form.Control required onChange={dataChange} value={data?.skillDescription ?? ''} />
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="skillMemo">
							<Form.Label>{t('Memo', 'Muistio')}</Form.Label>
							<Form.Control
								as="textarea"
								required
								placeholder={t('Memo', 'Muistio')}
								rows={3}
								onChange={dataChange}
								value={data?.skillMemo ?? ''}
							/>
						</Form.Group>
					</Row>

					<Row className="mb-1">
						<Form.Group as={Col} controlId="keywords">
							<Form.Label>{t('Keywords', 'Avainsanat')}</Form.Label>
							<CreatableSelect
								inputId="keywords"
								value={data?.tags?.map((tag) => ({ value: tag.codeItemId, label: tag.codeKey }))}
								onChange={handleTags}
								onCreateOption={handleCreateTag}
								options={tagOptions}
								{...creatableSelectTagSettings(t)}
							/>
						</Form.Group>
					</Row>

					<h4>{t('Attachments', 'Liitteet')}</h4>
					<AttachmentView
						section="skill"
						entityid={data?.skillId}
						attachments={data?.attachments}
						savedId={
							(actionType === 'POST_SKILL_SUCCEEDED' || actionType === 'POST_SKILL_EDIT_SUCCEEDED') && data?.skillId
						}
					/>

					{data?.skillId > 0 && data.history !== null && (
						<div className="ag-theme-material styles-ag h-450 m-0">
							<hr />
							<h4>{t('History', 'Historia')}</h4>
							<AgGridReact
								columnDefs={columnDefs}
								getLocaleText={(params) => getLocaleText(t, params)}
								rowData={data.history}
								animateRows
								suppressCellFocus
								enableCellTextSelection
								suppressContextMenu
								debug={import.meta.env.DEV}
							/>
						</div>
					)}
				</Modal.Body>
				<Modal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
					<Button testId="cancel" variant="cancel" onClick={handleClose}>
						{t('Cancel', 'Keskeytä')}
					</Button>
					<Button testId="save" variant="primary" onClick={handleSave} disabled={!data?.personId}>
						{t('Save', 'Tallenna')}
					</Button>
				</Modal.Footer>
			</Modal>
		)
	)
}
